import React, {useState} from 'react';
import styles from './form.module.scss';
import Button from "../Button";
import DropInput from "./DropInput";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from "react-i18next";
import Loader from "../Loader";

const MAX_FILE_SIZE = 3000000; // 3MB
const SUPPORTED_FORMATS = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

//const phoneRegExp = /^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){10,14}(\s*)?$/;

const fileValidation = (supported_formats, fileSizeLimit) => {
  return yup
    .mixed()
    .test("file", "form_errors.provide_file", (value) => {
      return value && value.length !== 0;
    })
    .test("fileSize", "form_errors.file_size", (value) => {
      return (value.length !== 0) && value[0].size <= fileSizeLimit;
    })
    .test("type", "form_errors.file_formats", (value) => {
      return supported_formats.find((format) => {
        return (value.length !== 0) && value[0].type === format;
      });
    })
}

const schema = yup
  .object()
  .shape({
    name: yup
      .string()
      .min(2, "form_errors.name_empty")
      .max(34, "form_errors.name_long")
      .required("form_errors.name_empty"),
    email:yup
      .string()
      .email("form_errors.email_format")
      .required("form_errors.email_empty"),
    phone: yup
      .string()
      .optional(),
    cv: fileValidation(SUPPORTED_FORMATS, MAX_FILE_SIZE),
    linkedin: yup
      .string()
      .optional()
      .test(
        'url',
        "form_errors.linkedin",
        value => {
          if (!value) return true;
          return value?.length > 8 && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('www.'));
        }
      ),
    portfolio: yup
      .string()
      .optional()
      .test(
        'url',
        "form_errors.portfolio",
        value => {
          if (!value) return true;
          return value?.length > 3 && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('www.'));
        }
      ),
    policyAgreements: yup.boolean().oneOf([true], "form_errors.policy"),
    consentProcessing: yup.boolean()
  });

const Form = ({ title, sendTo, setShowForm, setShowThanks, setErrorRequest }) => {
  const [t] = useTranslation("global");
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema)
  });

  const registerWithMask = useHookFormMask(register);

  const values = watch();

  const onSubmit = (data) => {
    setIsLoading(true);
    const formData = new FormData();
    const mailData = {
      to: sendTo,
      subject: `${data.title !== '' ? `Отклик на вакансию ${data.title}` : 'Отклик без вакансии (резерв)'}`,
      text: `
          <p><strong>Вакансия:&nbsp;</strong>${data.title !== '' ? data.title : '-'}</p>
          <p><strong>Имя Фамилия:&nbsp;</strong>${data.name}</p>
          <p><strong>E-mail:&nbsp;</strong>${data.email}</p>
          <p><strong>Телефон:&nbsp;</strong>${data.phone.length > 5 ? data.phone : `${t("form.not_filled")}`}</p>
          <p><strong>Профиль на LinkedIn:&nbsp;</strong>${data.linkedin.length > 0 ? data.linkedin : `${t("form.not_filled")}`}</p>
          <p><strong>Сопроводительное письмо:&nbsp;</strong>${data.coverLetter.length > 0 ? data.coverLetter : `${t("form.not_filled")}`}</p>
          ${data.title !== '' ? <p><strong>Как узнали о вакансии:&nbsp;</strong>${data.howDidYouKnow.length > 0 ? data.howDidYouKnow : `${t("form.not_filled")}`}</p> : ''}
          <p><strong>Подтверждение о прочтении политики конфиденциальности и согласие на обработку персональных данных*:&nbsp;</strong>${data.policyAgreements && `${t("form.confirmed")}`}</p>
          ${data.title !== '' ? <p><strong>Согласие на хранение персональных данных:&nbsp;</strong>${data.consentProcessing ? `${t("form.confirmed")}` : `${t("form.not_filled")}`}</p>: ''}
        `
    };

    formData.append('data', JSON.stringify(mailData));
    formData.append("file", data.cv[0]);
    fetch('https://redbark-api.onrender.com/api/send', {
      method: 'POST',
      body: formData
    })
      .then(response => response.json())
      .then(() => {
        setIsLoading(false);
        setShowForm(false);
        setShowThanks(true);
      })
      .catch(error => {
        console.error('Error:', error);
        setIsLoading(false);
        setErrorRequest(true);
        setShowForm(false);
        setShowThanks(true);
      });
  }

  return (
    <>
      <div className={styles.wrapper}>
        <button className={styles.close} onClick={() => setShowForm(false)}>
          <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.55134 36.4138L4.5192 35.6938L23.3345 16.8785L20.5061 14.0501L7.24 27.3162L6.88613 28.0028L1.85716 33.0318L0.388216 34.9444L2.47363 35.3824L2.55134 36.4138Z"/>
            <path d="M34.2182 1.58568L32.2503 2.30571L13.435 21.121L16.2634 23.9495L29.5295 10.6834L29.8834 9.99674L34.9124 4.96776L36.3813 3.05514L34.2959 2.61716L34.2182 1.58568Z"/>
            <path d="M34.603 36.4138L32.6351 35.6938L13.8198 16.8785L16.6482 14.0501L29.9143 27.3162L30.2682 28.0028L35.2971 33.0318L36.7661 34.9444L34.6807 35.3824L34.603 36.4138Z"/>
            <path d="M2.9361 1.58568L4.90397 2.30571L23.7193 21.121L20.8909 23.9495L7.62476 10.6834L7.2709 9.99674L2.24192 4.96776L0.772981 3.05514L2.85839 2.61716L2.9361 1.58568Z"/>
          </svg>
        </button>

        <div className={styles.inner}>
          <h1 className={styles.title}>{ title !== '' && title }</h1>
        </div>

        <form
          className={styles.inner}
          onSubmit={handleSubmit(onSubmit)}
        >
          <input {...register("title")} type="hidden" value={title}/>

          <label className={styles.row}>
            <input
              {...register("name")}
              className={`
                ${styles.input} 
                ${errors.name ? styles.error : values.name?.length >= 2 && !errors.name ? styles.complete : ''}
               `}
              type="text"
            />
            <span className={styles.label}>{t("form.name")} *</span>
            { errors.name && <span className={styles.errorMessage}>{t(errors.name?.message)}</span> }
            { errors.name && <span className={styles.redCross}></span> }
            { values.name?.length >= 2 && !errors.name && <span className={styles.blackCheck}></span> }
          </label>

          <label className={styles.row}>
            <input
              {...register("email")}
              className={`
                ${styles.input} 
                ${errors.email ? styles.error : values.email?.length > 5 && !errors.email ? styles.complete : ''}
              `}
              type="email"
            />
            <span className={styles.label}>{t("form.email")} *</span>
            { errors.email && <span className={styles.errorMessage}>{t(errors.email?.message)}</span> }
            { errors.email && <span className={styles.redCross}></span> }
            { values.email?.length > 5 && !errors.email && <span className={styles.blackCheck}></span> }
          </label>

          <label className={styles.row}>
            <input
              {...registerWithMask('phone', ['+[9][9][9][9][9][9][9][9][9][9][9][9][9][9]'], {
                required: false
              })}
              className={`
                ${styles.input}
              `}
              type="tel"
              placeholder={'+'}
            />
            <span className={styles.label}>{t("form.phone")}</span>
            { values.phone?.length > 5 && <span className={styles.blackCheck}></span> }
          </label>

          <div className={styles.row}>
            <DropInput control={control} name={'cv'} error={errors.cv} inTimeValue={values.cv} text={t("form.add_file")}/>
            <span className={styles.label}>{t("form.cv")} (max {MAX_FILE_SIZE / 1000000} mb) * </span>
            { errors.cv && <span className={styles.errorMessage}>{t(errors.cv?.message)}</span> }
            { errors.cv && <span className={styles.redCross}></span> }
            { values.cv?.length > 0 && !errors.cv && <span className={styles.blackCheck}></span> }
          </div>

          <label className={styles.row}>
            <textarea
              {...register("coverLetter")}
              className={`
                ${styles.input} ${styles.textarea}
                ${errors.coverLetter ? styles.error : values.coverLetter?.length > 4 && !errors.coverLetter ? styles.complete : ''}
              `}
              placeholder={t("form.optional")}
            ></textarea>
            <span className={styles.label}>{t("form.cover")}</span>
            { values.coverLetter?.length > 4 && !errors.coverLetter && <span className={styles.blackCheck}></span> }
          </label>

          <label className={styles.row}>
            <input
              {...register("linkedin")}
              className={`
                ${styles.input} 
                ${errors.linkedin ? styles.error : values.linkedin?.length > 8 && !errors.linkedin ? styles.complete : ''}
              `}
              type="url"
              placeholder={t("form.optional")}
            />
            <span className={styles.label}>{t("form.linkedin")}</span>
            { errors.linkedin && <span className={styles.errorMessage}>{t(errors.linkedin?.message)}</span> }
            { errors.linkedin && <span className={styles.redCross}></span> }
            { values.linkedin?.length > 8 && !errors.linkedin && <span className={styles.blackCheck}></span> }
          </label>

          <label className={styles.row}>
            <input
              {...register("portfolio")}
              className={`
                ${styles.input} 
                ${errors.portfolio ? styles.error : values.portfolio?.length > 3 && !errors.portfolio ? styles.complete : ''}
              `}
              type="url"
              placeholder={t("form.optional")}
            />
            <span className={styles.label}>{t("form.portfolio")}</span>
            { errors.portfolio && <span className={styles.errorMessage}>{t(errors.portfolio?.message)}</span> }
            { errors.portfolio && <span className={styles.redCross}></span> }
            { values.portfolio?.length > 3 && !errors.portfolio && <span className={styles.blackCheck}></span> }
          </label>

          { title !== '' &&
              <label className={styles.row}>
                <textarea
                  {...register("howDidYouKnow")}
                  className={`
                    ${styles.input} ${styles.textarea}
                    ${errors.howDidYouKnow ? styles.error : values.howDidYouKnow?.length > 2 && !errors.howDidYouKnow ? styles.complete : ''}
                  `}
                  placeholder={t("form.optional")}
                ></textarea>
                <span className={styles.label}>{t("form.how_did_you_know")}</span>
                { values.howDidYouKnow?.length > 2 && !errors.howDidYouKnow && <span className={styles.blackCheck}></span> }
              </label>
          }

          <label className={styles.checkBox} htmlFor="checkbox">
            <input
              {...register("policyAgreements")}
              id={'checkbox'}
              type="checkbox"
            />
            <span className={`${styles.checkBoxSquare} ${errors.policyAgreements ? styles.error : ''}`}></span>
            <span className={styles.checkBoxText}>{t("form.accept_politics")} <Link to={'/privacy'}>{t("form.politics_link_text")}</Link> {t("form.accept_politics_2")}  *</span>
            { errors.policyAgreements && <span className={styles.errorMessage}>{t(errors.policyAgreements?.message)}</span> }
          </label>

          { title !== '' &&
            <label className={styles.checkBox} htmlFor="checkbox_processing">
              <input
                {...register("consentProcessing")}
                id={'checkbox_processing'}
                type="checkbox"
              />
              <span className={`${styles.checkBoxSquare}`}></span>
              <span className={styles.checkBoxText}>
                {t("form.checkbox_processing")} <Link to={'/privacy'}>{t("form.checkbox_processing_politics_link")}</Link>.
                {t("form.checkbox_processing_2")} <a href={`mailto:${t("form.checkbox_processing_link")}`}>{t("form.checkbox_processing_link")}</a>.
              </span>
            </label>
          }

          <Button
            className={styles.button}
            text={t("buttons.send")}
            view={'light'}
          />
        </form>
      </div>
      {
        isLoading && <Loader transparent={true}/>
      }
    </>
  );
};

export default Form;